/* pb-cart.js — Headless unified cart via Storefront API */

(() => {
  // ---------- Config ----------
  const cfg = () => (window.PB_UNIFIED_CART || {});
  const ENDPOINT = () => cfg().endpoint;
  const TOKEN    = () => cfg().token;
  const COOKIE_DOMAIN = () => (cfg().cookieDomain || apex(location.hostname));
  const AUTO_HOOK = () => cfg().autoHookAddToCart === true;

  // Wait for endpoint + token
  const waitForConfig = async () => {
    let tries = 0;
    while (!ENDPOINT() || !TOKEN()) {
      await new Promise(r => setTimeout(r, 30));
      if (++tries > 200) throw new Error('[PBCart] Missing endpoint/token config');
    }
  };

  // Apex helper
  function apex(host) {
    const parts = String(host || location.hostname).split('.');
    return parts.length >= 2 ? `.${parts.slice(-2).join('.')}` : `.${host}`;
  }

  // ---------- Cookie ----------
  const CART_COOKIE = 'pb_cart_id';

  function setCookie(name, val, days = 30) {
    const d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie = `${name}=${encodeURIComponent(val)};expires=${d.toUTCString()};path=/;domain=${COOKIE_DOMAIN()};SameSite=None;Secure`;
  }

  function getCookie(name) {
    const hit = document.cookie.split('; ').find(r => r.startsWith(name + '='));
    return hit ? decodeURIComponent(hit.split('=')[1]) : null;
  }

  function delCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${COOKIE_DOMAIN()};SameSite=None;Secure`;
  }

  // ---------- GQL (with RETRY + THROTTLE handling) ----------
  async function gql(query, variables = {}) {
    await waitForConfig();

    let attempts = 0;

    while (attempts < 5) {
      const res = await fetch(ENDPOINT(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": TOKEN()
        },
        body: JSON.stringify({ query, variables })
      });

      const json = await res.json();

      // SUCCESS
      if (json.data && !json.errors) {
        return json.data;
      }

      // THROTTLED → retry
      if (json.errors && json.errors[0]?.extensions?.code === "THROTTLED") {
        const waitMs = 300 * (attempts + 1);
        console.warn(`[PBCart][THROTTLED] waiting ${waitMs}ms…`);
        await new Promise(r => setTimeout(r, waitMs));
        attempts++;
        continue;
      }

      // OTHER ERRORS
      throw new Error(`[PBCart] GQL error: ${JSON.stringify(json.errors)}`);
    }

    throw new Error("[PBCart] Failed after max retries (THROTTLED)");
  }

  // ---------- Cart Fragment ----------
  // NOTE:
  // - discountAllocations lives on CartLine (so automatic discounts can be computed) :contentReference[oaicite:2]{index=2}
  // - "fixed bundles" expose requiresComponents + components on ProductVariant :contentReference[oaicite:3]{index=3}
  // - "expanded bundles" / cart transform exposes nested lineComponents on CartLine (ComponentizableCartLine) :contentReference[oaicite:4]{index=4}
  const CART_FRAGMENT = `
  fragment CartMini on Cart {
    id
    checkoutUrl
    totalQuantity
    attributes { key value }

    cost {
      subtotalAmount { amount currencyCode }
      totalAmount { amount currencyCode }
    }

    lines(first: 100) {
      edges {
        node {
          id
          quantity
          attributes { key value }

          discountAllocations {
            discountedAmount { amount currencyCode }
          }

          cost {
            subtotalAmount { amount currencyCode }
            totalAmount { amount currencyCode }
          }

          merchandise {
            __typename
            ... on ProductVariant {
              id
              title
              sku
              image { url altText }
              price { amount currencyCode }
              product { id title handle vendor }

              requiresComponents
              components(first: 20) {
                nodes {
                  quantity
                  productVariant {
                    id
                    title
                    sku
                    image { url altText }
                    product { id title handle vendor }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;


  // ---------- API OPS ----------
  async function cartCreate() {
    const d = await gql(`mutation { cartCreate(input:{}) { cart { ...CartMini } } } ${CART_FRAGMENT}`);
    return d.cartCreate.cart;
  }

  async function cartQuery(id) {
    const d = await gql(`query($id:ID!){ cart(id:$id){ ...CartMini } } ${CART_FRAGMENT}`, { id });
    return d.cart;
  }

  async function cartLinesAdd(cartId, lines) {
    const d = await gql(`
      mutation($cartId:ID!, $lines:[CartLineInput!]!) {
        cartLinesAdd(cartId:$cartId, lines:$lines) { cart { ...CartMini } }
      }
      ${CART_FRAGMENT}
    `, { cartId, lines });
    return d.cartLinesAdd.cart;
  }

  async function cartLinesUpdate(cartId, lines) {
    const d = await gql(`
      mutation($cartId:ID!,$lines:[CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId:$cartId, lines:$lines) { cart { ...CartMini } }
      }
      ${CART_FRAGMENT}
    `, { cartId, lines });
    return d.cartLinesUpdate.cart;
  }

  async function cartLinesRemove(cartId, lineIds) {
    const d = await gql(`
      mutation($cartId:ID!,$lineIds:[ID!]!) {
        cartLinesRemove(cartId:$cartId, lineIds:$lineIds) { cart { ...CartMini } }
      }
      ${CART_FRAGMENT}
    `, { cartId, lineIds });
    return d.cartLinesRemove.cart;
  }

  async function cartAttributesUpdate(cartId, attributes) {
    const d = await gql(`
      mutation($cartId:ID!,$attributes:[AttributeInput!]!) {
        cartAttributesUpdate(cartId:$cartId, attributes:$attributes) { cart { ...CartMini } }
      }
      ${CART_FRAGMENT}
    `, { cartId, attributes });
    return d.cartAttributesUpdate.cart;
  }

  // ---------- Boot ----------
  let _cartId = null;
  let _cartPromise = null;

  async function ensureCart() {
    await waitForConfig();

    if (_cartId) return _cartId;

    // try cookie
    const cookieId = getCookie(CART_COOKIE);
    if (cookieId) {
      try {
        const c = await cartQuery(cookieId);
        if (c?.id) {
          _cartId = c.id;
          _cartPromise = Promise.resolve(c);
          return _cartId;
        }
      } catch (err) {}
    }

    // create new cart
    let created = await cartCreate();

    // ensure first-touch attribute is set on cart
    created = await ensureFirstTouchAttribute(created);

    _cartId = created.id;
    _cartPromise = Promise.resolve(created);
    setCookie(CART_COOKIE, _cartId);

    document.dispatchEvent(updatedEvent(created, "boot"));
    return _cartId;
  }

  function gidFromVariant(id) {
    const s = String(id);
    return s.startsWith("gid://")
      ? s
      : `gid://shopify/ProductVariant/${s.replace(/\D/g, "")}`;
  }

  function updatedEvent(cart, source = "update") {
    return new CustomEvent("pb:cart:updated", {
      detail: { source, cart }
    });
  }

  // ---------- First-touch brand → Cart attribute ----------
  const FIRST_TOUCH_COOKIE = 'pb_first_touch_brand';
  const FIRST_TOUCH_KEY    = 'pb_first_touch_brand';

  let _firstTouchSyncing = false;
  let _firstTouchDoneForCartId = null;

  function getFirstTouchBrand() {
    return getCookie(FIRST_TOUCH_COOKIE) || 'brauzz';
  }

  // Only update if missing/different, and only once per cart id (prevents spam)
  async function ensureFirstTouchAttribute(cart) {
    try {
      if (!cart?.id) return cart;

      const brand = getFirstTouchBrand();

      // Prevent repeat calls for same cart id
      if (_firstTouchDoneForCartId === cart.id) return cart;
      if (_firstTouchSyncing) return cart;

      const existing = (cart.attributes || []).find(a => a.key === FIRST_TOUCH_KEY)?.value;

      if (existing === brand) {
        _firstTouchDoneForCartId = cart.id;
        return cart;
      }

      _firstTouchSyncing = true;

      const updated = await cartAttributesUpdate(cart.id, [
        { key: FIRST_TOUCH_KEY, value: String(brand) }
      ]);

      _firstTouchDoneForCartId = updated?.id || cart.id;
      _firstTouchSyncing = false;

      return updated || cart;
    } catch (e) {
      _firstTouchSyncing = false;
      console.warn('[PBCart] ensureFirstTouchAttribute failed', e);
      return cart;
    }
  }

  // ---------- PUBLIC API ----------
  const PBCart = {
    async refresh() {
      await ensureCart();
      try {
        let c = await cartQuery(_cartId);
        c = await ensureFirstTouchAttribute(c);
        _cartPromise = Promise.resolve(c);
        document.dispatchEvent(updatedEvent(c, "refresh"));
        return c;
      } catch (e) {
        const newCart = await cartCreate();
        _cartId = newCart.id;
        setCookie(CART_COOKIE, _cartId);
        _cartPromise = Promise.resolve(newCart);
        document.dispatchEvent(updatedEvent(newCart, "refresh:new"));
        return newCart;
      }
    },

    async addLine(variantIdOrGid, qty = 1, attributes = []) {
      const UnifiedCartDrawer2 = document.querySelector('#UnifiedCartDrawer');

      await ensureCart();

      const gid = gidFromVariant(variantIdOrGid);
      const payload = {
        merchandiseId: gid,
        quantity: Math.max(1, Number(qty) || 1)
      };
      if (attributes?.length) payload.attributes = attributes;

      console.log("=== PBCart.addLine CALLED ===", { variant: variantIdOrGid, qty });

      let c = await cartLinesAdd(_cartId, [payload]);
      c = await ensureFirstTouchAttribute(c);

      console.log("=== PBCart.addLine RESPONSE ===", c);

      UnifiedCartDrawer2?.classList.add('is-open');

      _cartPromise = Promise.resolve(c);
      document.dispatchEvent(updatedEvent(c, "add"));
      return c;
    },

    async updateLine(lineId, qty) {
      await ensureCart();
      const c = await cartLinesUpdate(_cartId, [{ id: lineId, quantity: qty }]);
      _cartPromise = Promise.resolve(c);
      document.dispatchEvent(updatedEvent(c, "update"));
      return c;
    },

    async removeLine(lineId) {
      await ensureCart();
      const c = await cartLinesRemove(_cartId, [lineId]);
      _cartPromise = Promise.resolve(c);
      document.dispatchEvent(updatedEvent(c, "remove"));
      return c;
    },

    async setAttributes(obj = {}) {
      await ensureCart();
      const attrs = Object.entries(obj).map(([k, v]) => ({ key: k, value: String(v) }));
      const c = await cartAttributesUpdate(_cartId, attrs);
      _cartPromise = Promise.resolve(c);
      document.dispatchEvent(updatedEvent(c, "attributes"));
      return c;
    },

    current() {
      return _cartPromise || this.refresh();
    },

    getCartId() {
      return _cartId || getCookie(CART_COOKIE);
    },

    setCartId(id) {
      _cartId = id || null;
      if (_cartId) setCookie(CART_COOKIE, _cartId);
      else delCookie(CART_COOKIE);
      _cartPromise = _cartId ? cartQuery(_cartId) : null;
      return _cartId;
    },

    async checkoutUrl() {
      let c = await this.refresh();
      c = await ensureFirstTouchAttribute(c);
      return c?.checkoutUrl || "";
    },

    async clear() {
      delCookie(CART_COOKIE);
      _cartId = null;
      _cartPromise = null;
      const c = await this.refresh();
      return c;
    }
  };

  window.PBCart = PBCart;

  // ---------- AUTO HOOK ----------
  if (AUTO_HOOK()) {
    document.addEventListener(
      "submit",
      async (e) => {
        const form = e.target?.closest('form[action*="/cart/add"]');
        if (!form) return;

        const idInput = form.querySelector('input[name="id"]');
        const qtyInput = form.querySelector('input[name="quantity"]');

        const variant = idInput?.value;
        const qty = parseInt(qtyInput?.value || "1", 10);

        if (!variant) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        await PBCart.addLine(variant, qty);
      },
      { capture: true }
    );
  }

  // ---------- INIT ----------
  (async () => {
    try {
      await ensureCart();
    } catch (e) {
      console.error("[PBCart][boot]", e);
    }
  })();
})();
